import { ApiService } from 'src/app/services/api/api.service';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { SignupService } from 'src/app/services/signup/signup.service';
import Utils, { GetHelper, payloadsEqual } from 'src/app/helpers/utils';
import { ToastService } from 'src/app/services/toast/toast.service';
import { mainActivityItems, riskItems, webCopItems } from 'src/app/data/risk';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-signup-unified-company-complementary-risk',
  templateUrl: './signup-unified-company-complementary-risk.component.html',
  styleUrls: ['./signup-unified-company-complementary-risk.component.scss'],
})
export class SignupUnifiedCompanyComplementaryRiskComponent implements OnInit {
  @Input() userRole: string;

  form = this.formBuilder.group({
    operating_limit: [null, [Validators.required, Validators.min(1)]],
    // web_corp_migrated_framework: [null, [Validators.required]],
    risk: [null, [Validators.required]],
    observations: [null, []],
    equity: [null, [Validators.min(1)]],
  });

  loading = true;
  riskId = null;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  event: any;

  riskOptions = riskItems;
  webCopOptions = webCopItems;
  mainAcityOptions = mainActivityItems;

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private api: ApiService,
    private toast: ToastService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  /**
   * Configura a validação do campo equity baseado em isFund
   * Se for fundo, equity é obrigatório. Caso contrário, não é obrigatório.
   */
  setEquityValidation(): void {
    const equityControl = this.form.controls.equity;
    const isFund = this.signupService.getIsFund();

    if (isFund) {
      // Se for fundo, adiciona Validators.required
      equityControl.setValidators([Validators.required, Validators.min(1)]);
    } else {
      // Se não for fundo, remove Validators.required, mantém apenas min(1)
      equityControl.setValidators([]);
    }
    equityControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Recalcula validação do grupo após PATCH dos dados (evita "Seguir" desabilitado com campos ok).
   */
  private syncRiskFormStatusAfterLoad(): void {
    this.setEquityValidation();
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
    this.form.updateValueAndValidity({ emitEvent: true });
    this.signupService.setCompanyFormStatus(
      'complementaryRisk',
      this.form.valid,
    );
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    // Verificar se é cedente ou prestador e redirecionar se necessário
    const isAssignor = this.router.url.includes('assignor');
    const isProvider = this.router.url.includes('provider');
    if (isAssignor || isProvider) {
      // Se for cedente ou prestador, redireciona para representantes

      this.signupService.setComplementaryCompanyStep(
        'complementaryRepresentatives',
      );
      this.signupService.changeCompanyStepEvent.emit('representatives');

      return;
    }

    this.signupService.choicesFetchedEvent.subscribe((choices) => {
      this.loading = false;
    });

    this.event = this.signupService.sendComplementaryCompanyData.subscribe(
      (value) => {
        if (value === 'complementaryRisk') {
          this.sendData();
        }
      },
    );

    // Configurar validação condicional do equity baseado em isFund
    this.setEquityValidation();

    this.getDataAndFillForm();
    // Listener para mudanças de valor
    this.form.valueChanges.subscribe(() => {
      console.log(Utils.getErrors(this.form));

      this.signupService.setCompanyFormStatus(
        'complementaryRisk',
        this.form.valid,
      );
    });
  }

  async getDataAndFillForm() {
    this.loading = true;
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota',
        );
        this.loading = false;
        setTimeout(() => this.syncRiskFormStatusAfterLoad(), 0);
        return;
      }

      const res = await this.api.get<any>({
        route: 'api/registration/risk-information/',
        token: true,
        params: {
          register_uuid: routeId,
        },
      });

      // A resposta pode vir como array ou objeto único
      let riskData = null;
      if (Array.isArray(res)) {
        riskData = res.length > 0 ? res[0] : null;
      } else {
        riskData = res?.data || res || null;
      }

      if (riskData && Object.keys(riskData).length > 0) {
        this.riskId = riskData.id ?? null;

        this.form.patchValue({
          operating_limit: riskData.operating_limit ?? null,
          //   web_corp_migrated_framework: Number(riskData.web_corp_migrated_framework),
          risk: riskData.risk ?? null,
          observations: riskData.observations ?? null,
          equity: riskData.equity ?? null,
        });

        this.initialPayload = this.buildPayloadFromForm();
      } else {
        this.initialPayload = null;
      }
    } catch (error) {
      this.initialPayload = null;
    } finally {
      this.loading = false;
      // Após ciclo de CD (currency-input / app-select), recalcula FormGroup e habilita "Seguir" se válido
      setTimeout(() => this.syncRiskFormStatusAfterLoad(), 0);
    }
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      ...valuesHelper.getRemainingObject(),
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show('error', 'Erro', 'Register UUID não encontrado na rota');
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      this.signupService.setComplementaryCompanyStep(
        'complementaryRepresentatives',
      );
      this.signupService.changeCompanyStepEvent.emit('representatives');
      return;
    }

    this.signupService.loading = true;

    try {
      let apiRoute: string;
      const params: any = { register_uuid: routeId };

      if (this.riskId) {
        apiRoute = `api/registration/risk-information/${this.riskId}/`;

        const response = await this.api.put<any>({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.initialPayload = this.buildPayloadFromForm();
        this.toast.show(
          'info',
          'Sucesso',
          response.message || 'Dados salvos com sucesso!',
        );
      } else {
        apiRoute = 'api/registration/risk-information/';

        const response = await this.api.post<any>({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.riskId = response.data?.id ?? null;
        this.initialPayload = this.buildPayloadFromForm();
        this.toast.show(
          'info',
          'Sucesso',
          response.message || 'Dados salvos com sucesso!',
        );
      }

      this.signupService.setComplementaryCompanyStep(
        'complementaryRepresentatives',
      );
      this.signupService.changeCompanyStepEvent.emit('representatives');
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Ocorreu um erro ao salvar os dados',
      );
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }
}
